import { store } from 'store';
import { Mode } from 'types';

import { isValidName } from './helpers';
import { sophieDebugLog, sophieDebugSetNameFlag, sophieDebugUpdateStt } from './sophieDebug';
import { AssistantAction, SophieInstructions } from './types';

// ── Mode switch mapping (backend mode ID → frontend Mode) ────────────────────

const MODE_SWITCH_MAP: Record<string, Mode> = {
  swap: 'swap',
  portal: 'portal',
  portfolio: 'cockpit',
  autopilot: 'autopilot',
  pilot: 'pilot',
  home: 'main',
  whitepaper: 'whitepaper',
  sale: 'sale',
};

const BLOCKED_MODE_SWITCHES = new Set<Mode>(['sale']);

// ── Callback interface ─────────────────────────────────────────────────────────

export interface WsEventHandlers {
  /** Audio data for playback (voice only) */
  onAudioDelta?: (delta: string) => void;

  /** VAD interrupted the response (voice only) */
  onInterrupted?: () => void;

  /** User started speaking (voice only) */
  onSpeechStarted?: () => void;

  /** User stopped speaking (voice only) */
  onSpeechStopped?: () => void;

  /** STT transcript delta (voice only) */
  onSttDelta?: (delta: string) => void;

  /** Mode switch command from backend */
  onModeSwitch?: (mode: Mode) => void;

  /** UI action command from backend */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onAction?: (action: AssistantAction, params?: Record<string, any>) => void;
}

// ── Universal WS event handler ─────────────────────────────────────────────────

/**
 * Shared event handler for Sophie's WS events.
 * Used by both VoiceAssistant and TextChat — no code duplication.
 *
 * Text events (delta, done) are handled via store's chatMessages.
 * Voice-specific events (audio, STT, mic) are delegated via optional callbacks.
 */
export function handleAssistantWsEvent(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  event: Record<string, any>,
  handlers?: WsEventHandlers
): void {
  const { assistantStore } = store;

  switch (event.type) {
    // ── Text streaming (voice API) ───────────────────────────────────────────
    case 'unmute.response.text.delta.ready': {
      if (event.delta) {
        const id = assistantStore.ensureAssistantMsg();
        assistantStore.appendToChatMessage(id, event.delta);
      }
      break;
    }

    // ── Text streaming (text API) ─────────────────────────────────────────
    case 'text_delta': {
      console.log('[Sophie] text_delta payload:', JSON.stringify(event));
      const delta = event.delta || event.text;
      if (delta) {
        const id = assistantStore.ensureAssistantMsg();
        assistantStore.appendToChatMessage(id, delta);
      }
      break;
    }

    // ── Response complete (voice API — text channel) ──────────────────────
    case 'response.text.done': {
      const text = event.text || assistantStore.finalizeAssistantMsg();
      if (text) {
        if (event.text) assistantStore.finalizeAssistantMsg();
        assistantStore.saveMessage('assistant', text);
        sophieDebugLog('🤖 LLM said: "' + text.substring(0, 200) + '"');
      }
      break;
    }

    // ── Response complete (text API / audio API) ──────────────────────────
    case 'response.done': {
      assistantStore.setSophiePhase('listening');
      const text = assistantStore.finalizeAssistantMsg();
      if (text) {
        assistantStore.saveMessage('assistant', text);
        sophieDebugLog('🤖 LLM said: "' + text.substring(0, 200) + '"');
      }
      break;
    }

    case 'response.audio.done': {
      assistantStore.setSophiePhase('listening');
      const text = assistantStore.finalizeAssistantMsg();
      if (text) {
        assistantStore.saveMessage('assistant', text);
      }
      break;
    }

    // ── Audio data (voice only) ─────────────────────────────────────────────
    case 'response.audio.delta':
      if (assistantStore.sophiePhase !== 'speaking') {
        assistantStore.setSophiePhase('speaking');
      }
      if (handlers?.onAudioDelta && event.delta) handlers.onAudioDelta(event.delta);
      break;

    // ── VAD interrupt (voice only) ──────────────────────────────────────────
    case 'unmute.interrupted_by_vad':
      assistantStore.setSophiePhase('listening');
      if (handlers?.onInterrupted) handlers.onInterrupted();
      break;

    // ── Mic events (voice only) ─────────────────────────────────────────────
    case 'input_audio_buffer.speech_started':
      assistantStore.setSophiePhase('listening');
      if (handlers?.onSpeechStarted) handlers.onSpeechStarted();
      break;

    case 'input_audio_buffer.speech_stopped':
      assistantStore.setSophiePhase('thinking');
      if (handlers?.onSpeechStopped) handlers.onSpeechStopped();
      break;

    // ── STT transcription (voice only) ──────────────────────────────────────
    case 'conversation.item.input_audio_transcription.delta':
      if (handlers?.onSttDelta && event.delta) handlers.onSttDelta(event.delta);
      break;

    // ── STT correction (voice only) ─────────────────────────────────────────
    case 'unmute.transcription_corrected': {
      const { original, corrected } = event;
      if (original && corrected && original !== corrected) {
        assistantStore.replaceLastUserText(String(original), String(corrected));
        sophieDebugLog('✏️ STT corrected: "' + original + '" → "' + corrected + '"');
      }
      break;
    }

    // ── News digest story (voice + text) ────────────────────────────────────
    case 'unmute.news_story':
    case 'news_story': {
      const storyId =
        typeof event.story_id === 'number' ? event.story_id : parseInt(event.story_id ?? '0', 10);
      const text = event.text as string;
      if (text?.trim()) {
        assistantStore.addNewsStoryMsg(storyId, text.trim());
      }
      break;
    }

    // ── Mode switch (via callback — ignored during history replay) ─────────
    case 'unmute.mode_switch':
    case 'mode_switch': {
      const targetMode = MODE_SWITCH_MAP[event.mode];
      if (targetMode) {
        if (BLOCKED_MODE_SWITCHES.has(targetMode)) {
          sophieDebugLog('⛔ Blocked mode switch → ' + targetMode);
          break;
        }

        if (handlers?.onModeSwitch) {
          handlers.onModeSwitch(targetMode);
          sophieDebugLog('🚀 Mode switch → ' + targetMode);
        }
      } else {
        console.warn('[Sophie] Unknown mode:', event.mode);
      }
      break;
    }

    // ── UI action (via callback — ignored during history replay) ───────────
    case 'unmute.action':
    case 'action': {
      const actionId = event.action;
      if (actionId && handlers?.onAction) {
        handlers.onAction(String(actionId) as AssistantAction, event.params);
        sophieDebugLog('⚡ Action → ' + actionId);
      }
      break;
    }

    // ── Name detection (universal) ──────────────────────────────────────────
    case 'unmute.save_user_name':
      if (event.name && isValidName(event.name)) {
        const ok = assistantStore.setUserName(String(event.name));
        if (ok) {
          sophieDebugLog('✅ NAME FROM BACKEND: ' + assistantStore.userName);
          sophieDebugSetNameFlag('true (from backend: "' + assistantStore.userName + '")');
        }
      } else {
        sophieDebugLog('❌ Invalid name from backend: "' + event.name + '"');
      }
      break;

    // ── Server error ────────────────────────────────────────────────────────
    case 'error':
      console.error('[Sophie] Server error:', JSON.stringify(event, null, 2));
      sophieDebugLog('❗ Server error: ' + (event.error?.message || event.type));
      break;

    // ── Silently consumed events ────────────────────────────────────────────
    case 'response.text.delta':
    case 'response.start':
    case 'session.updated':
    case 'response.created':
    case 'unmute.response.audio.delta.ready':
      break;

    // ── Unknown events ──────────────────────────────────────────────────────
    default:
      console.log('[Sophie] Event:', event.type);
  }
}

// ── Session setup helpers ──────────────────────────────────────────────────────

/**
 * Send session.update to the voice WS.
 */
export function sendSessionUpdate(ws: WebSocket, userId: string, instructions: string): void {
  ws.send(
    JSON.stringify({
      type: 'session.update',
      session: {
        instructions: {
          type: 'constant',
          text: instructions,
          language: null,
        },
        voice: 'ears/p003/freeform_speech_01.wav',
        allow_recording: false,
        user_id: userId,
      },
    })
  );
}

/**
 * Send session.update to the text WS.
 */
export function sendTextSessionUpdate(
  ws: WebSocket,
  userId: string,
  userName: string | undefined,
  instructions: string
): void {
  ws.send(
    JSON.stringify({
      type: 'session.update',
      session: {
        instructions: { text: instructions },
        user_id: userId,
        user_name: userName || undefined,
      },
    })
  );
}

/**
 * Log session init info to the debug panel — shared between voice and text.
 */
export function logSessionInit(sophie: SophieInstructions): void {
  const hasName = !!sophie.userName;
  sophieDebugSetNameFlag(hasName ? 'true (from storage: "' + sophie.userName + '")' : 'false');
  sophieDebugLog(
    'Session init: userName=' +
      (sophie.userName || '(none)') +
      ' firstTime=' +
      sophie.isFirstTime +
      ' returningNoName=' +
      sophie.isReturningNoName
  );
}

/**
 * Log WS connection events to the debug panel.
 */
export function logWsConnecting(url: string): void {
  sophieDebugLog('🔌 Connecting WS: ' + url);
}

export function logWsConnected(): void {
  sophieDebugLog('🔌 WS connected');
}

export function logWsError(error?: string): void {
  sophieDebugLog('❗ WS error: ' + (error || 'connection failed'));
}

export function logWsClosed(code: number, reason?: string): void {
  sophieDebugLog('🔌 WS closed: ' + code + ' ' + (reason || ''));
}

/**
 * Build WS URL with user params — shared between voice and text.
 */
export function buildWsUrl(baseUrl: string, userId: string): string {
  const params = new URLSearchParams({
    user_id: userId,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  });
  return baseUrl + '?' + params.toString();
}

// ── STT helpers (voice only, but shared via this module) ────────────────────

export { sophieDebugUpdateStt };
